import { sketchWallTemplatesHtml, bindSketchWallTemplates } from './sketch_wall_template_ui.js';
import { S } from '../app/app_state.js?v=20260817c';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { recordUndo } from '../services/history_service.js?v=20260903c';
import {
  ensureSketchWallState,
  sketchWallById,
  sketchNodeById,
  sketchWallSideLength,
  deleteSketchWall,
  updateSketchWallThickness,
  updateSketchWallHeight,
  updateSketchWallHatchType,
  updateSketchWallColor,
  clearSketchWallSelection,
  selectSketchWallEntity,
  sketchWallDrawDefaults,
  updateSketchWallDrawDefaults,
  sketchWallOpeningsWithLabels,
  updateSketchWallOpening,
  deleteSketchWallOpening,
  sketchWallFurring,
  setSketchWallFurringEnabled,
  updateSketchWallFurring,
  updateSketchWallFurringColor,
  matchSketchWallFurringToMainWall,
  toggleSketchWallFurringSide
} from './sketch_wall_state.js?v=20260904f';
import { solveSketchWalls } from './sketch_wall_solver.js?v=20260706a';
import {
  sketchWallDrawToolActive,
  sketchWallOpeningToolActive,
  toggleSketchWallOpeningTool,
  sketchWallSplitToolActive,
  sketchWallParallelToolActive,
  toggleSelectedSketchWallFixed,
  alignSelectedSketchWall,
  splitSelectedSketchWall,
  parallelSelectedSketchWall
} from './sketch_wall_interaction.js?v=20260903c';
import { colorCatalogEntries, colorPickerAllowedCatalogsForUsage } from '../../../colors/color_catalog_registry.js?v=20260720a';
import { bindColorPicker, renderColorPicker } from '../../../colors/color_picker.js?v=20260720a';

// These buttons act on the currently-selected wall (S.selection.sketchWall.wallId)
// when clicked — not an "arm a tool, then click the canvas" flow. Highlighting
// reflects the selected wall's actual flags, not "which tool is armed".
function sketchWallConstraintToolsHtml() {
  const wall = sketchWallById(S.selection?.sketchWall?.wallId);
  // Horizontal/Vertikal only "stick" (persist as a real angle constraint) on
  // an underdetermined wall — once fully determined, clicking them is a
  // one-time alignment of the whole group (see alignSelectedSketchWall), so
  // the button must never show as active for a fully-determined wall.
  const isUnderdetermined = wall?.free !== false;
  const tools = [
    { id: 'fixed', label: 'Fixiert', active: Boolean(wall?.fixed) },
    { id: 'horizontal', label: 'Horizontal', active: isUnderdetermined && Boolean(wall?.horizontal) },
    { id: 'vertical', label: 'Vertikal', active: isUnderdetermined && Boolean(wall?.vertical) },
    // Parallel has no persistent flag of its own (see
    // handleGlobalSketchWallParallelClick) — "active" here just means the
    // tool is currently armed for THIS wall, waiting for a reference click.
    { id: 'parallel', label: 'Parallel', active: sketchWallParallelToolActive() && S.ui?.sketchWallParallelWallId === wall?.id }
  ];
  return `
    <div class="sketch-wall-position-grid">
      ${tools.map(tool => `
        <button type="button" class="btn sketch-wall-constraint-btn${tool.active ? ' is-selected' : ''}" data-sketch-wall-constraint-tool="${tool.id}">
          ${tool.label}
        </button>
      `).join('')}
    </div>
  `;
}

// Every top-level sidebar card for the sketch-wall panel (Parametrische
// Wände / Vormauerung / Ausschnitte) is a native <details>, but only ever
// ONE at a time — accordion-style, not independently collapsible. Which one
// is open lives in the single S.ui.sketchWallActiveSidebarCard value (set
// by sketchWallSidebarCardForSelection whenever the selection changes, e.g.
// clicking a wall body / an Ausschnitt / the Vormauerung body in the plan —
// see bindSketchWallInteraction's `furring` flag and
// bindSketchWallOpeningInteraction), so it also survives the panel's own
// re-renders (e.g. after every edit) instead of resetting every time.
function sketchWallCardIsOpen(cardId) {
  return (S.ui?.sketchWallActiveSidebarCard || 'main') === cardId;
}

function sketchWallConfigCardHtml(cardId, title, bodyHtml) {
  return `
    <details class="card sketch-wall-config-card" data-sketch-wall-card="${cardId}" ${sketchWallCardIsOpen(cardId) ? 'open' : ''}>
      <summary class="sketch-wall-config-card-summary"><span>${title}</span></summary>
      <div class="sketch-wall-config-card-body">${bodyHtml}</div>
    </details>
  `;
}

function sketchWallDetailRenderPoint(nodeId) {
  const node = sketchNodeById(nodeId);
  return node ? { x: node.x, z: node.z } : { x: 0, z: 0 };
}

function sketchWallHatchTypeOptionsHtml(hatchType) {
  return colorCatalogEntries('texturen_wand')
    .map(entry => `<option value="${entry.code}" ${hatchType === entry.code ? 'selected' : ''}>${entry.name}</option>`)
    .join('');
}

// Which catalogs show up here is now admin-configurable (Administration →
// Farben und Dekore → "Parametrische Wand"/"Wand (Farbe)") — seeded to only
// RAL Classic and NCS by default, since this tints the chosen Wandart's real
// photo texture (see getWall3dMaterialByVariant), not a standalone flat
// color, so Standard/Dekor/Texturen swatches would be redundant with the
// Wandart picker above.
function sketchWallColorPickerHtml(wallColor) {
  return renderColorPicker({
    id: 'sketchWallColorPicker',
    value: wallColor,
    allowedCatalogs: colorPickerAllowedCatalogsForUsage('sketchWallColorPicker'),
    allowDefault: true,
    defaultLabel: 'Standard / keine Farbe'
  });
}

// Admin-configurable (Administration → Farben und Dekore → "Parametrische
// Wand"/"Vormauerung") — seeded to every catalog except Standard by default:
// unlike the main wall's Farbe picker above, the Vormauerung has no separate
// Wandart picker of its own, so a decor/texture swatch is its only way to
// look genuinely different from the main wall it's attached to; Standard
// itself has no real use case here.
function sketchWallFurringColorPickerHtml(wallColor) {
  return renderColorPicker({
    id: 'sketchWallFurringColorPicker',
    value: wallColor,
    allowedCatalogs: colorPickerAllowedCatalogsForUsage('sketchWallFurringColorPicker'),
    allowDefault: true,
    defaultLabel: 'Standard / keine Farbe'
  });
}

// Türe/Fenster only differ in their preset dimensions (see
// SKETCH_WALL_OPENING_PRESETS in sketch_wall_state.js) — once placed, an
// opening is just three numbers (Breite/Höhe/Ab Boden), never tagged as
// door or window, so this same row UI edits either kind identically and a
// "Türe" can freely become a "Fenster" by editing Ab Boden.
function sketchWallOpeningRowHtml(wall, opening) {
  return `
    <div class="sketch-wall-opening-row" data-sketch-wall-opening-id="${opening.id}">
      <div class="sketch-wall-detail-row"><strong>${opening.label}</strong>
        <button type="button" class="btn danger" data-sketch-wall-opening-delete="${opening.id}">Löschen</button>
      </div>
      <label class="sketch-wall-detail-row">
        <span>Breite</span>
        <input type="number" min="1" step="1" data-sketch-wall-opening-field="width" value="${Math.round(opening.width)}">
      </label>
      <label class="sketch-wall-detail-row">
        <span>Höhe</span>
        <input type="number" min="1" step="1" data-sketch-wall-opening-field="height" value="${Math.round(opening.height)}">
      </label>
      <label class="sketch-wall-detail-row">
        <span>Ab Boden</span>
        <input type="number" min="0" step="1" data-sketch-wall-opening-field="bottomOffset" value="${Math.round(opening.bottomOffset)}">
      </label>
    </div>
  `;
}

// Vormauerung: a furring wall doubling one face of the wall (e.g. to bury a
// Spülkasten) — a simple on/off plus thickness/height/side, not a
// placeable sub-object like an Ausschnitt, so this card only shows the
// dimension controls once it's actually switched on.
function sketchWallFurringCardHtml(wall) {
  if (!wall) return '';
  const furring = sketchWallFurring(wall);
  const body = `
    <div class="sketch-wall-tool-grid">
      <button type="button" class="btn sketch-wall-constraint-btn${furring.enabled ? ' is-selected' : ''}" data-sketch-wall-furring-toggle="on">Ein</button>
      <button type="button" class="btn sketch-wall-constraint-btn${!furring.enabled ? ' is-selected' : ''}" data-sketch-wall-furring-toggle="off">Aus</button>
    </div>
    ${furring.enabled ? `
      <button type="button" class="btn" id="btnSketchWallFurringSide">Richtung ändern</button>
      <label class="sketch-wall-detail-row">
        <span>Aufdopplung</span>
        <input type="number" min="1" step="1" id="inputSketchWallFurringThickness" value="${Math.round(furring.thickness)}">
      </label>
      <label class="sketch-wall-detail-row">
        <span>Höhe ab Boden</span>
        <input type="number" min="1" step="10" id="inputSketchWallFurringHeight" value="${Math.round(furring.height)}">
      </label>
      <button type="button" class="btn" id="btnSketchWallFurringMatchMain">Gleich wie Hauptwand</button>
      ${sketchWallFurringColorPickerHtml(furring.wallColor)}
    ` : ''}
  `;
  return sketchWallConfigCardHtml('furring', 'Vormauerung', body);
}

function sketchWallOpeningsCardHtml(wall) {
  if (!wall) return '';
  const doorActive = sketchWallOpeningToolActive() && S.ui.sketchWallTool === 'door';
  const windowActive = sketchWallOpeningToolActive() && S.ui.sketchWallTool === 'window';
  const openings = sketchWallOpeningsWithLabels(wall);
  const body = `
    <div class="sketch-wall-tool-grid">
      <button type="button" class="btn sketch-wall-constraint-btn${doorActive ? ' is-selected' : ''}" data-sketch-wall-opening-tool="door">Türe</button>
      <button type="button" class="btn sketch-wall-constraint-btn${windowActive ? ' is-selected' : ''}" data-sketch-wall-opening-tool="window">Fenster</button>
    </div>
    <p class="sketch-wall-hint">Werkzeug anklicken, dann auf eine Wand klicken. Ausschnitte lassen sich am Punkt im Grundriss verschieben.</p>
    ${openings.map(opening => sketchWallOpeningRowHtml(wall, opening)).join('')}
  `;
  return sketchWallConfigCardHtml('openings', 'Ausschnitte', body);
}

// With a wall selected, these fields edit that wall directly. With the draw
// tool armed but nothing drawn yet, they edit sketchWallDrawDefaults()
// instead, so thickness/height/Wandart/Farbe can be set up front — every
// wall drawn afterwards while the tool stays armed starts from these values
// (see createSketchWall's call site in sketch_wall_interaction.js). Returns
// the raw "Details" and "Farbe und Dekor" section bodies (no card/label
// wrapper of their own) plus the resolved wall, since both sections now live
// inside the single merged "Parametrische Wände" card built by
// sketchWallPanelHtml, while wall stays needed there to also gate the
// separate Vormauerung/Ausschnitte cards.
function sketchWallDetailAndColorSections() {
  const sel = S.selection?.sketchWall;
  const wall = sel?.wallId ? sketchWallById(sel.wallId) : null;
  if (sel?.wallId && !wall) return { detailsHtml: '', colorHtml: '', wall: null };
  if (!wall && !sketchWallDrawToolActive()) return { detailsHtml: '', colorHtml: '', wall: null };

  const defaults = wall ? null : sketchWallDrawDefaults();
  const thickness = wall ? wall.thickness : defaults.thickness;
  const height = wall ? wall.height : defaults.height;
  const hatchType = wall ? wall.hatchType : defaults.hatchType;
  const wallColor = wall ? wall.wallColor : defaults.wallColor;

  const lengthsHtml = wall ? `
    <div class="sketch-wall-detail-row"><span>Innenlänge</span><strong>${Math.round(sketchWallSideLength(wall, sketchWallDetailRenderPoint, 1))} mm</strong></div>
    <div class="sketch-wall-detail-row"><span>Außenlänge</span><strong>${Math.round(sketchWallSideLength(wall, sketchWallDetailRenderPoint, -1))} mm</strong></div>
  ` : '';
  const statusHtml = wall ? `
    <div class="sketch-wall-detail-row"><span>Status</span><strong>${wall.free === false ? 'Vollständig definiert' : 'Unterdefiniert'}</strong></div>
    <button type="button" class="btn sketch-wall-constraint-btn${sketchWallSplitToolActive() ? ' is-selected' : ''}" id="btnSketchWallSplitSelected">Schnitt</button>
    <button type="button" class="btn danger" id="btnSketchWallDeleteSelected">Wand löschen</button>
  ` : '';

  const detailsHtml = `
    ${lengthsHtml}
    <label class="sketch-wall-detail-row">
      <span>Dicke</span>
      <input type="number" min="1" step="1" id="inputSketchWallThickness" value="${Math.round(thickness)}">
    </label>
    <label class="sketch-wall-detail-row">
      <span>Höhe</span>
      <input type="number" min="1" step="10" id="inputSketchWallHeight" value="${Math.round(height)}">
    </label>
    <label class="sketch-wall-detail-row">
      <span>Wandart</span>
      <select id="selectSketchWallHatchType">${sketchWallHatchTypeOptionsHtml(hatchType)}</select>
    </label>
    ${statusHtml}
  `;
  const colorHtml = sketchWallColorPickerHtml(wallColor);

  return { detailsHtml, colorHtml, wall };
}

function sketchWallPanelHtml() {
  const { detailsHtml, colorHtml, wall } = sketchWallDetailAndColorSections();
  const mainBody = `
    <div class="sketch-wall-section">
      <span class="sketch-wall-section-label">Positionierung</span>
      ${sketchWallConstraintToolsHtml()}
    </div>
    ${detailsHtml ? `
    <div class="sketch-wall-section">
      <span class="sketch-wall-section-label">Details</span>
      ${detailsHtml}
    </div>` : ''}
    ${colorHtml ? `
    <div class="sketch-wall-section">
      <span class="sketch-wall-section-label">Farbe und Dekor</span>
      ${colorHtml}
    </div>` : ''}
  `;
  return `
    ${sketchWallConfigCardHtml('main', 'Parametrische Wände', mainBody)}
    ${sketchWallDrawToolActive() ? sketchWallConfigCardHtml('templates', 'Vorlagen', sketchWallTemplatesHtml()) : ''}
    ${sketchWallFurringCardHtml(wall)}
    ${sketchWallOpeningsCardHtml(wall)}
  `;
}

function bindSketchWallPanelEvents() {
  bindSketchWallTemplates();
  // Accordion: opening one card closes every other directly via the DOM
  // (no full re-render needed for that) and records it as the active card;
  // collapsing the currently-active one just clears that record, so the
  // next selection-driven open (see sketchWallSidebarCardForSelection)
  // still picks the right card instead of this stale collapse fighting it.
  document.querySelectorAll('[data-sketch-wall-card]').forEach(card => {
    card.addEventListener('toggle', () => {
      if (!card.isConnected) return;
      const cardId = card.getAttribute('data-sketch-wall-card');
      if (card.open) {
        S.ui.sketchWallActiveSidebarCard = cardId;
        document.querySelectorAll('[data-sketch-wall-card]').forEach(other => {
          if (other !== card) other.open = false;
        });
      } else if (S.ui.sketchWallActiveSidebarCard === cardId) {
        S.ui.sketchWallActiveSidebarCard = null;
      }
    });
  });
  document.querySelectorAll('[data-sketch-wall-constraint-tool]').forEach(button => {
    button.addEventListener('click', () => {
      const tool = button.getAttribute('data-sketch-wall-constraint-tool');
      if (tool === 'fixed') toggleSelectedSketchWallFixed();
      else if (tool === 'horizontal' || tool === 'vertical') alignSelectedSketchWall(tool);
      else if (tool === 'parallel') parallelSelectedSketchWall();
    });
  });
  document.getElementById('btnSketchWallSplitSelected')?.addEventListener('click', () => {
    splitSelectedSketchWall();
  });
  document.getElementById('btnSketchWallDeleteSelected')?.addEventListener('click', () => {
    const wallId = S.selection?.sketchWall?.wallId;
    if (!wallId) return;
    recordUndo('Wand löschen');
    deleteSketchWall(wallId);
    solveSketchWalls();
    clearSketchWallSelection();
    AppRuntime.requestAppRefresh(false, 'sketch-wall-delete-panel');
  });
  document.getElementById('inputSketchWallThickness')?.addEventListener('change', event => {
    const value = Number(event.target.value);
    if (!(value > 0)) return;
    const wallId = S.selection?.sketchWall?.wallId;
    if (!wallId) {
      updateSketchWallDrawDefaults({ thickness: value });
      AppRuntime.requestAppRefresh(false, 'sketch-wall-thickness-defaults');
      return;
    }
    recordUndo('Wanddicke ändern');
    updateSketchWallThickness(wallId, value);
    solveSketchWalls();
    AppRuntime.requestAppRefresh(false, 'sketch-wall-thickness-panel');
  });
  document.getElementById('inputSketchWallHeight')?.addEventListener('change', event => {
    const value = Number(event.target.value);
    if (!(value > 0)) return;
    const wallId = S.selection?.sketchWall?.wallId;
    if (!wallId) {
      updateSketchWallDrawDefaults({ height: value });
      AppRuntime.requestAppRefresh(false, 'sketch-wall-height-defaults');
      return;
    }
    recordUndo('Wandhöhe ändern');
    updateSketchWallHeight(wallId, value);
    AppRuntime.requestAppRefresh(false, 'sketch-wall-height-panel');
  });
  document.getElementById('selectSketchWallHatchType')?.addEventListener('change', event => {
    const wallId = S.selection?.sketchWall?.wallId;
    if (!wallId) {
      updateSketchWallDrawDefaults({ hatchType: event.target.value });
      AppRuntime.requestAppRefresh(false, 'sketch-wall-hatch-defaults');
      return;
    }
    recordUndo('Wandart ändern');
    updateSketchWallHatchType(wallId, event.target.value);
    AppRuntime.requestAppRefresh(false, 'sketch-wall-hatch-panel');
  });
  bindColorPicker('sketchWallColorPicker', wallColor => {
    const wallId = S.selection?.sketchWall?.wallId;
    if (!wallId) {
      updateSketchWallDrawDefaults({ wallColor });
      AppRuntime.requestAppRefresh(false, 'sketch-wall-color-defaults');
      return;
    }
    recordUndo('Wandfarbe ändern');
    updateSketchWallColor(wallId, wallColor);
    AppRuntime.requestAppRefresh(false, 'sketch-wall-color-panel');
  });
  document.querySelectorAll('[data-sketch-wall-furring-toggle]').forEach(button => {
    button.addEventListener('click', () => {
      const wallId = S.selection?.sketchWall?.wallId;
      if (!wallId) return;
      const enabled = button.getAttribute('data-sketch-wall-furring-toggle') === 'on';
      recordUndo(enabled ? 'Vormauerung einschalten' : 'Vormauerung ausschalten');
      setSketchWallFurringEnabled(wallId, enabled);
      AppRuntime.requestAppRefresh(false, 'sketch-wall-furring-toggle');
    });
  });
  document.getElementById('btnSketchWallFurringSide')?.addEventListener('click', () => {
    const wallId = S.selection?.sketchWall?.wallId;
    if (!wallId) return;
    recordUndo('Vormauerung Richtung ändern');
    toggleSketchWallFurringSide(wallId);
    AppRuntime.requestAppRefresh(false, 'sketch-wall-furring-side');
  });
  document.getElementById('inputSketchWallFurringThickness')?.addEventListener('change', event => {
    const wallId = S.selection?.sketchWall?.wallId;
    const value = Number(event.target.value);
    if (!wallId || !(value > 0)) return;
    recordUndo('Vormauerung Aufdopplung ändern');
    updateSketchWallFurring(wallId, { thickness: value });
    AppRuntime.requestAppRefresh(false, 'sketch-wall-furring-thickness');
  });
  document.getElementById('inputSketchWallFurringHeight')?.addEventListener('change', event => {
    const wallId = S.selection?.sketchWall?.wallId;
    const value = Number(event.target.value);
    if (!wallId || !(value > 0)) return;
    recordUndo('Vormauerung Höhe ändern');
    updateSketchWallFurring(wallId, { height: value });
    AppRuntime.requestAppRefresh(false, 'sketch-wall-furring-height');
  });
  document.getElementById('btnSketchWallFurringMatchMain')?.addEventListener('click', () => {
    const wallId = S.selection?.sketchWall?.wallId;
    if (!wallId) return;
    recordUndo('Vormauerung Farbe von Hauptwand übernehmen');
    matchSketchWallFurringToMainWall(wallId);
    AppRuntime.requestAppRefresh(false, 'sketch-wall-furring-match-main');
  });
  bindColorPicker('sketchWallFurringColorPicker', wallColor => {
    const wallId = S.selection?.sketchWall?.wallId;
    if (!wallId) return;
    recordUndo('Vormauerung Farbe ändern');
    updateSketchWallFurringColor(wallId, wallColor);
    AppRuntime.requestAppRefresh(false, 'sketch-wall-furring-color');
  });
  document.querySelectorAll('[data-sketch-wall-opening-tool]').forEach(button => {
    button.addEventListener('click', () => {
      toggleSketchWallOpeningTool(button.getAttribute('data-sketch-wall-opening-tool'));
    });
  });
  document.querySelectorAll('[data-sketch-wall-opening-delete]').forEach(button => {
    button.addEventListener('click', () => {
      const wallId = S.selection?.sketchWall?.wallId;
      const openingId = button.getAttribute('data-sketch-wall-opening-delete');
      if (!wallId || !openingId) return;
      recordUndo('Ausschnitt löschen');
      deleteSketchWallOpening(wallId, openingId);
      // Keep the wall selected, but drop a now-stale reference to the
      // opening that was just deleted (e.g. if it was the marked one).
      selectSketchWallEntity({ wallId });
      AppRuntime.requestAppRefresh(false, 'sketch-wall-opening-delete');
    });
  });
  document.querySelectorAll('[data-sketch-wall-opening-field]').forEach(input => {
    input.addEventListener('change', event => {
      const wallId = S.selection?.sketchWall?.wallId;
      const row = input.closest('[data-sketch-wall-opening-id]');
      const openingId = row?.getAttribute('data-sketch-wall-opening-id');
      const field = input.getAttribute('data-sketch-wall-opening-field');
      const value = Number(event.target.value);
      if (!wallId || !openingId || !Number.isFinite(value)) return;
      if (field !== 'bottomOffset' && !(value > 0)) return;
      if (field === 'bottomOffset' && value < 0) return;
      recordUndo('Ausschnitt ändern');
      updateSketchWallOpening(wallId, openingId, { [field]: value });
      AppRuntime.requestAppRefresh(false, 'sketch-wall-opening-edit');
    });
  });
}

function renderSketchWallPanel() {
  ensureSketchWallState();
  const sel = S.selection?.sketchWall;
  const hasSelection = Boolean(sel?.wallId || sel?.nodeId || sel?.angleId);
  if (!sketchWallDrawToolActive() && !sketchWallOpeningToolActive() && !hasSelection) return false;
  const host = document.getElementById('selectionPanel');
  if (!host) return false;
  host.className = 'sketch-wall-panel';
  host.innerHTML = sketchWallPanelHtml();
  bindSketchWallPanelEvents();
  return true;
}

export { renderSketchWallPanel };
